// Reads the `desk.header` page state (SPEC §17.2) a desk publishes with `ncp.state.set({"desk.header": …})`. WP-DESK-HOST.
//
// The state comes from a sandboxed page, so it is read as untrusted data: every field is checked for shape and length, links must stay
// inside the workspace (a path starting with one "/"), tool names must be ones the page declares, and anything else is dropped. Nothing is
// filled in: a field the page did not send is simply not drawn.
import type { DeskChipKind, DeskHeaderState, DeskKey, DeskNumber, DeskTone, ModuleKey, Sensitivity } from "@ncp/page-sdk/desk";
import type { LocalisedText } from "@ncp/page-sdk/types";
import { isModuleKey, moduleHref } from "./drawsOn";

const SENS: readonly Sensitivity[] = ["public", "internal", "restricted", "confidential"];
const CHIP_KINDS: readonly DeskChipKind[] = ["state", "date", "figures", "late", "blocked", "info"];
const TONES: readonly DeskTone[] = ["good", "warn", "serious", "crit", "info"];
const GROUPS = ["waiting", "underway", "new", "delivered"] as const;
const ROLES = ["prepared", "reviewed", "supervised"] as const;

const str = (v: unknown, max = 200): string | null => (typeof v === "string" && v.trim() ? v.trim().slice(0, max) : null);
const num = (v: unknown, min = 0, max = 100_000): number | null => (typeof v === "number" && Number.isFinite(v) && v >= min && v <= max ? v : null);
const arr = <T>(v: unknown, max: number, read: (x: unknown) => T | null): T[] => (Array.isArray(v) ? v.slice(0, max).map(read).filter((x): x is T => x !== null) : []);

/** A bilingual label: `{en, ar?}` (or a plain string, read as English). */
export function bi(v: unknown, max = 200): LocalisedText | null {
  if (typeof v === "string") return str(v, max) ? { en: str(v, max)! } : null;
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const en = str(o.en, max);
  const ar = str(o.ar, max);
  if (!en && !ar) return null;
  return { en: en ?? ar!, ...(ar ? { ar } : {}) };
}

/** A link a page may hand the host: a workspace path ("/p/…", "/m/…", "/knowledge/…"), never another origin or a scheme. */
export function safeHref(v: unknown): string | null {
  const s = str(v, 400);
  if (!s || !s.startsWith("/") || s.startsWith("//") || /[\s\\]/.test(s)) return null;
  return s;
}

const isoDate = (v: unknown): string | null => {
  const s = str(v, 40);
  return s && !Number.isNaN(Date.parse(s)) ? s : null;
};

/** The header a desk published, checked; null when the page sent none (or nothing usable). */
export function readDeskHeader(state: Record<string, unknown> | null | undefined, desk: { key: DeskKey; n: DeskNumber }, tools: readonly string[] = []): DeskHeaderState | null {
  const raw = state?.["desk.header"];
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const d = (o.deliverable ?? {}) as Record<string, unknown>;
  const title = bi(d.title, 240);
  if (!title) return null;
  const classification = SENS.includes(o.classification as Sensitivity) ? (o.classification as Sensitivity) : "internal";

  const chips = arr(o.chips, 8, (c) => {
    const x = (c ?? {}) as Record<string, unknown>;
    const label = bi(x.label, 120);
    const kind = CHIP_KINDS.includes(x.kind as DeskChipKind) ? (x.kind as DeskChipKind) : "info";
    if (!label) return null;
    const tone = TONES.includes(x.tone as DeskTone) ? (x.tone as DeskTone) : undefined;
    const count = num(x.count) ?? undefined;
    return { kind, label, ...(tone ? { tone } : {}), ...(count !== undefined ? { count } : {}) };
  });

  const stageRaw = o.stage as Record<string, unknown> | undefined;
  const steps = stageRaw ? arr(stageRaw.steps, 12, (s) => bi(s, 80)) : [];
  const current = stageRaw ? num(stageRaw.current, 0, 11) : null;
  const stage = steps.length && current !== null ? { steps, current: Math.min(current, steps.length - 1), ...(bi(stageRaw?.nextGate, 120) ? { nextGate: bi(stageRaw?.nextGate, 120)! } : {}) } : undefined;

  const roles = arr(o.roles, 3, (r) => {
    const x = (r ?? {}) as Record<string, unknown>;
    const role = ROLES.includes(x.role as (typeof ROLES)[number]) ? (x.role as (typeof ROLES)[number]) : null;
    if (!role) return null;
    const initials = str(x.initials, 3) ?? undefined;
    const name = bi(x.name, 80) ?? undefined;
    return { role, ...(initials ? { initials } : {}), ...(name ? { name } : {}) };
  });

  const drawsOn = arr(o.drawsOn, 6, (m) => {
    const x = (m ?? {}) as Record<string, unknown>;
    const key = x.module === "knowledge" ? "kb" : x.module;
    if (!isModuleKey(key)) return null;
    const label = bi(x.label, 60);
    const count = num(x.count) ?? undefined;
    return { module: key as ModuleKey, primary: x.primary === true, label: label ?? { en: key }, href: safeHref(x.href) ?? moduleHref(key), ...(count !== undefined ? { count } : {}) };
  });

  const readinessRaw = o.readiness as Record<string, unknown> | undefined;
  const done = readinessRaw ? num(readinessRaw.done, 0, 999) : null;
  const total = readinessRaw ? num(readinessRaw.total, 1, 999) : null;
  const readiness = done !== null && total !== null ? { done: Math.min(done, total), total, blocked: readinessRaw?.blocked === true } : undefined;

  const versionRaw = o.version as Record<string, unknown> | undefined;
  const vn = versionRaw ? num(versionRaw.n, 0, 10_000) : null;
  const vat = versionRaw ? isoDate(versionRaw.at) : null;
  const version = vn !== null && vat ? { n: vn, at: vat } : undefined;

  const routinesRaw = o.routines as Record<string, unknown> | undefined;
  const rc = routinesRaw ? num(routinesRaw.count, 0, 999) : null;
  const routines = rc !== null ? { count: rc, ...(isoDate(routinesRaw?.next) ? { next: isoDate(routinesRaw?.next)! } : {}) } : undefined;

  const switcherRaw = o.switcher as Record<string, unknown> | undefined;
  const groups = switcherRaw ? arr(switcherRaw.groups, 4, (g) => {
    const x = (g ?? {}) as Record<string, unknown>;
    const key = GROUPS.includes(x.key as (typeof GROUPS)[number]) ? (x.key as (typeof GROUPS)[number]) : null;
    const label = bi(x.label, 60);
    if (!key || !label) return null;
    const items = arr(x.items, 12, (i) => {
      const y = (i ?? {}) as Record<string, unknown>;
      const id = str(y.id, 80);
      const t = bi(y.title, 160);
      const href = safeHref(y.href);
      if (!id || !t || !href) return null;
      const number = str(y.number, 40) ?? undefined;
      return { id, title: t, href, ...(number ? { number } : {}) };
    });
    return items.length ? { key, label, items } : null;
  }) : [];

  const primaryRaw = o.primary as Record<string, unknown> | undefined;
  const tool = primaryRaw ? str(primaryRaw.tool, 64) : null;
  const primaryLabel = primaryRaw ? bi(primaryRaw.label, 40) : null;
  const primary = tool && primaryLabel && tools.includes(tool) ? { label: primaryLabel, tool, ...(primaryRaw?.input !== undefined ? { input: primaryRaw.input } : {}) } : undefined;

  const composeRaw = o.compose as Record<string, unknown> | undefined;
  const types = composeRaw ? arr(composeRaw.types, 8, (t) => {
    const x = (t ?? {}) as Record<string, unknown>;
    const key = str(x.key, 48);
    const label = bi(x.label, 60);
    return key && label ? { key, label } : null;
  }) : [];

  const number = str(d.number, 40) ?? undefined;
  const kind = bi(d.kind, 80) ?? { en: "" };
  const href = safeHref(d.href) ?? undefined;
  const due = o.due === null ? null : isoDate(o.due) ?? undefined;
  return {
    v: 1,
    desk: desk.key,
    number: desk.n,
    deliverable: { id: str(d.id, 80) ?? "", title, kind, ...(number ? { number } : {}), ...(href ? { href } : {}) },
    chips,
    classification,
    drawsOn,
    ...(stage ? { stage } : {}),
    ...(roles.length ? { roles } : {}),
    ...(due !== undefined ? { due } : {}),
    ...(version ? { version } : {}),
    ...(readiness ? { readiness } : {}),
    ...(routines ? { routines } : {}),
    ...(groups.length ? { switcher: { groups } } : {}),
    ...(primary ? { primary } : {}),
    ...(types.length ? { compose: { types } } : {}),
    ...(o.present === true ? { present: true } : {}),
  };
}

/** The page's copilot context line (`copilot.context`, process-kit `useAppCopilot`): the deliverable chip before a desk publishes its header. */
export function readContextLine(state: Record<string, unknown> | null | undefined): LocalisedText | null {
  const c = (state?.copilot as Record<string, unknown> | undefined)?.context;
  return bi(c, 160);
}

export const pickText = (t: LocalisedText | null | undefined, ar: boolean): string => (t ? ((ar && t.ar) || t.en) : "");
/** The other language's form of a label, when the page gave both (the secondary name beside the H1). */
export const otherText = (t: LocalisedText | null | undefined, ar: boolean): string | null => {
  if (!t?.ar || t.ar === t.en) return null;
  return ar ? t.en : t.ar;
};
